import { Button, Textarea, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { refusalMessage } from "../api/manufacturing";

/** A note as both production orders and parts keep them (0055, 0063). */
export interface NoteDTO {
  id: number;
  body: string;
  /** Read only: set from the signed-in account. */
  created_by_account_id?: number | null;
  created_by_username?: string | null;
  created_at?: string;
  updated_at?: string;
  /** Read only: the signed-in person wrote it. */
  is_mine?: boolean;
}

/** Where a record's notes are read and written. */
export interface NoteStore<T extends NoteDTO> {
  list: () => Promise<T[]>;
  create: (body: string) => Promise<unknown>;
  update: (note: T) => Promise<unknown>;
  remove: (id: number) => Promise<unknown>;
}

/**
 * A record's notes: who wrote what, and when, oldest first, with a box to add
 * one at the bottom. A note is its author's: only they see Edit and Delete,
 * and the server refuses anyone else.
 */
export function NotesPanel<T extends NoteDTO>({
  queryKey,
  store,
  editable,
}: {
  queryKey: readonly unknown[];
  store: NoteStore<T>;
  editable: boolean;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const key = queryKey;
  const notes = useQuery({ queryKey: key, queryFn: store.list });

  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState<{ id: number; body: string } | null>(null);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: key });
  const failed = (failure: unknown) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));

  const add = useMutation({
    mutationFn: () => store.create(draft.trim()),
    onSuccess: () => {
      setDraft("");
      refresh();
    },
    onError: failed,
  });
  const save = useMutation({
    mutationFn: (note: T) => store.update(note),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
    onError: failed,
  });
  const remove = useMutation({
    mutationFn: (id: number) => store.remove(id),
    onSuccess: refresh,
    onError: failed,
  });

  const stamp = (value?: string) =>
    value ? new Date(value).toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" }) : "";

  const rows = notes.data ?? [];
  return (
    <div className="flex flex-col gap-3 p-4 lg:p-6">
      {notes.isLoading && <p className="text-[13px] text-ink-muted">{t("table.loading", "Loading")}</p>}
      {!notes.isLoading && rows.length === 0 && (
        <p className="text-[13px] text-ink-muted">{t("lot.notes.none", "No note yet.")}</p>
      )}
      {rows.map((note) => (
        <article key={note.id} className="rounded-md border border-line bg-surface px-4 py-3">
          <header className="mb-1 flex flex-wrap items-baseline gap-x-2 text-[12px] text-ink-muted">
            <span className="font-medium text-ink">{note.created_by_username ?? t("lot.notes.unknown-author", "Unknown")}</span>
            <span className="tabular">{stamp(note.created_at)}</span>
            {note.updated_at && note.created_at && note.updated_at !== note.created_at && (
              <span>{t("lot.notes.edited", "edited {{when}}", { when: stamp(note.updated_at) })}</span>
            )}
            {editable && note.is_mine && editing?.id !== note.id && (
              <span className="ml-auto flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => setEditing({ id: note.id, body: note.body })}>
                  {t("lot.notes.edit", "Edit")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    const yes = await confirm({
                      title: t("lot.notes.delete.title", "Delete this note?"),
                      message: t("lot.notes.delete.message", "It cannot be brought back."),
                      variant: "danger",
                      confirmLabel: t("lot.notes.delete", "Delete"),
                    });
                    if (yes) remove.mutate(note.id);
                  }}
                >
                  {t("lot.notes.delete", "Delete")}
                </Button>
              </span>
            )}
          </header>
          {editing?.id === note.id ? (
            <div className="flex flex-col gap-2">
              <Textarea
                rows={4}
                value={editing.body}
                onChange={(event) => setEditing({ id: note.id, body: event.target.value })}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  loading={save.isPending}
                  disabled={editing.body.trim() === ""}
                  onClick={() => save.mutate({ ...note, body: editing.body.trim() })}
                >
                  {t("lot.notes.save", "Save")}
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setEditing(null)}>
                  {t("dialog.cancel", "Cancel")}
                </Button>
              </div>
            </div>
          ) : (
            <p className="whitespace-pre-wrap text-[13px] text-ink">{note.body}</p>
          )}
        </article>
      ))}
      {editable && (
        <div className="flex flex-col gap-2 rounded-md border border-line bg-surface-sunk p-3">
          <Textarea
            rows={4}
            value={draft}
            placeholder={t("lot.notes.placeholder", "Add a note: what happened, what to watch, who called")}
            onChange={(event) => setDraft(event.target.value)}
          />
          <div>
            <Button size="sm" loading={add.isPending} disabled={draft.trim() === ""} onClick={() => add.mutate()}>
              {t("lot.notes.add", "Add note")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
